import { createContext, lazy, Suspense, use, useCallback, useRef, useState } from "react";

import type { QueuedToast } from "./toast-host.tsx";
import type { ToastOptions } from "@estate-platform/ui/toast";
import type { ReactElement, ReactNode } from "react";

export type ShowToast = (options: ToastOptions) => void;

const ToastContext = createContext<ShowToast | null>(null);
ToastContext.displayName = "ToastContext";

const ToastHost = lazy(async () => {
  const { ToastHost: component } = await import("./toast-host.tsx");
  return { default: component };
});

// WHY: toasts outlive the page that raised them, and the toast system loads only when the first one is shown.
export const ToasterProvider = ({ children }: { readonly children: ReactNode }): ReactElement => {
  const [queue, setQueue] = useState<readonly QueuedToast[]>([]);
  const [isMounted, setIsMounted] = useState(false);
  const nextIdRef = useRef(0);
  const show = useCallback<ShowToast>((options) => {
    nextIdRef.current += 1;
    const id = nextIdRef.current;
    setQueue((queued) => [...queued, { id, options }]);
    setIsMounted(true);
  }, []);
  const shown = useCallback((ids: ReadonlySet<number>) => {
    setQueue((queued) => queued.filter((toast) => !ids.has(toast.id)));
  }, []);
  return (
    <ToastContext value={show}>
      {children}
      {isMounted && (
        // eslint-disable-next-line local/no-null-suspense-fallback -- the queue holds a toast until its host lands; it reports what the screen already shows, so nothing waits on it
        <Suspense fallback={null}>
          <ToastHost queue={queue} onShown={shown} />
        </Suspense>
      )}
    </ToastContext>
  );
};

export const useShowToast = (): ShowToast => {
  const show = use(ToastContext);
  if (show === null) {
    throw new Error("useShowToast needs a ToasterProvider");
  }
  return show;
};
